import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { Camera, ChevronLeft, ChevronRight, Check, Loader2, AlertCircle, ImagePlus } from 'lucide-react';

const compressImage = (file, maxWidth = 1200, quality = 0.7) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(compressedDataUrl);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

const QCWizardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [checklist, setChecklist] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  const canEditCustomer = user?.role === 'Admin' || user?.role === 'Service Admin';
  
  const [allCustomers, setAllCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(true);
  
  const [allAssets, setAllAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(true);

  const [formData, setFormData] = useState({
    order_type: '',
    qc_event_type: '',
    customer_or_company_name: '',
    asset_id: '',
    product_category: '',
    model_number: '',
    serial_number: '',
    ram_size: '',
    ram_type: '',
    ssd_size: '',
    ssd_type: '',
    upgrades_done: false,
    ram_added_or_changed: false,
    ram_serial_number: '',
    old_ram_returned_to_store: false,
    ssd_added_or_changed: false,
    ssd_serial_number: '',
    old_ssd_returned_to_store: false,
    upgrade_notes: '',
    upgrade_approved_by: '',
    second_check_technician: '',
    qc_checklist: {},
    photos: [],
    remarks: '',
  });

  useEffect(() => {
    fetchAssets();
    fetchTechnicians();
  }, []);

  useEffect(() => {
    if (formData.product_category) {
      fetchChecklist(formData.product_category);
    }
  }, [formData.product_category]);

  const fetchAssets = async () => {
    setAssetsLoading(true);
    try {
      const response = await api.get('/assets');
      const sorted = response.data.sort((a, b) => 
        (a.asset_id || '').localeCompare(b.asset_id || '', undefined, { sensitivity: 'base' })
      );
      setAllAssets(sorted);
      setAssets(sorted);
    } catch (error) {
      console.error('Failed to fetch assets:', error);
    } finally {
      setAssetsLoading(false);
    }
  };

  const fetchTechnicians = async () => {
    try {
      const response = await api.get('/technicians');
      setTechnicians(response.data.filter(t => t.full_name !== user?.full_name));
    } catch (error) {
      console.error('Failed to fetch technicians:', error);
    }
  };

  const fetchCustomers = async () => {
    setCustomersLoading(true);
    try {
      api.get('/customers/sync-google-sheet').catch(() => {});
      await new Promise(r => setTimeout(r, 500));
      const response = await api.get('/customers');
      const sorted = response.data.sort((a, b) => 
        (a.billing_name || '').localeCompare(b.billing_name || '', undefined, { sensitivity: 'base' })
      );
      setAllCustomers(sorted);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setCustomersLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleAssetSelect = (assetId) => {
    const asset = allAssets.find((a) => a.asset_id === assetId);
    if (asset) {
      setFormData((prev) => ({
        ...prev,
        asset_id: assetId,
        product_category: asset.asset_type,
        model_number: asset.model_name,
        serial_number: asset.serial_number,
        ram_size: asset.ram_size || '',
        ram_type: asset.ram_type || '',
        ssd_size: asset.ssd_size || '',
        ssd_type: asset.ssd_type || '',
      }));
    }
  };

  const handleCustomerSelect = (customerName) => {
    setFormData((prev) => ({
      ...prev,
      customer_or_company_name: customerName,
    }));
  };

  const customerItems = useMemo(() => 
    allCustomers.map((c) => ({
      value: c.billing_name,
      label: c.billing_name,
      searchValue: `${c.billing_name} ${c.contact_person || ''} ${c.phone_number || ''}`,
      contact_person: c.contact_person,
      phone_number: c.phone_number,
    })),
    [allCustomers]
  );

  const assetItems = useMemo(() => 
    allAssets.map((a) => ({
      value: a.asset_id,
      label: `${a.asset_id} - ${a.brand} ${a.model_name}`,
      searchValue: `${a.asset_id} ${a.brand} ${a.model_name} ${a.serial_number} ${a.asset_type}`,
      serial_number: a.serial_number,
      asset_type: a.asset_type,
    })),
    [allAssets]
  );

  const fetchChecklist = async (productCategory) => {
    try {
      const response = await api.get(`/checklists/${productCategory}`);
      setChecklist(response.data.checklist_data);
      initializeChecklist(response.data.checklist_data);
    } catch (error) {
      console.error('Failed to fetch checklist:', error);
      toast.error('Checklist not found for this product');
    }
  };

  const initializeChecklist = (checklistData) => {
    const initialized = {};
    Object.keys(checklistData).forEach((stage) => {
      initialized[stage] = {};
      checklistData[stage].forEach((item) => {
        initialized[stage][item] = { answer: '', notes: '' };
      });
    });
    // Explicitly add Original OS tracker
    initialized['OriginalOSSection'] = {
      'Original OS': { answer: '', notes: '' }
    };
    setFormData((prev) => ({ ...prev, qc_checklist: initialized }));
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    const timestamp = new Date().toISOString();
    const formattedTimestamp = new Date().toLocaleString();
    
    for (const file of files) {
      try {
        const compressedData = await compressImage(file, 1200, 0.7);
        setFormData((prev) => ({
          ...prev,
          photos: [
            ...prev.photos,
            {
              category: 'General',
              data: compressedData,
              timestamp: timestamp,
              formatted_timestamp: formattedTimestamp,
              filename: file.name,
            },
          ],
        }));
      } catch (error) {
        console.error('Failed to process image:', error);
        toast.error(`Failed to process ${file.name}`);
      }
    }
    if (e.target) {
      e.target.value = '';
    }
  };

  const handleCameraCapture = () => {
    if (cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  };

  const handleGalleryUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const updateChecklistItem = (stage, item, field, value) => {
    setFormData((prev) => ({
      ...prev,
      qc_checklist: {
        ...prev.qc_checklist,
        [stage]: {
          ...prev.qc_checklist[stage],
          [item]: {
            ...prev.qc_checklist[stage]?.[item],
            [field]: value,
          },
        },
      },
    }));
    setValidationErrors([]);
  };

  const validateChecklist = () => {
    if (!checklist) return { valid: true, errors: [] };
    
    const errors = [];
    Object.keys(checklist).forEach((stage) => {
      checklist[stage].forEach((item) => {
        const answer = formData.qc_checklist[stage]?.[item]?.answer;
        if (!answer || answer === '') {
          errors.push({ stage, item });
        }
      });
    });
    
    const osAns = formData.qc_checklist['OriginalOSSection']?.['Original OS']?.answer;
    if (!osAns || osAns === '') {
      errors.push({ stage: 'OriginalOSSection', item: 'Original OS' });
    }
    
    return { valid: errors.length === 0, errors };
  };

  const handleSubmit = async () => {
    const { valid, errors } = validateChecklist();
    
    if (!valid) {
      setValidationErrors(errors);
      toast.error(`Please complete all mandatory QC checks before submitting. ${errors.length} item(s) remaining.`);
      return;
    }
    
    if (!formData.second_check_technician) {
      toast.error('Please assign a second check technician');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/qc', formData);
      toast.success('QC entry created successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to create QC entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to create QC entry');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { number: 1, title: 'Order Context' },
    { number: 2, title: 'Upgrades', show: ['Laptop', 'Desktop'].includes(formData.product_category) },
    { number: 3, title: 'QC Checklist' },
    { number: 4, title: 'Photos & Review' },
  ].filter(step => step.show !== false);

  const getCurrentVisualStep = () => {
    if (!['Laptop', 'Desktop'].includes(formData.product_category)) {
      if (currentStep === 1) return 1;
      if (currentStep === 3) return 2;
      if (currentStep === 4) return 3;
    }
    return currentStep;
  };
  
  const isLastStep = () => currentStep === 4;

  const canProceed = () => {
    if (currentStep === 1) {
      return formData.order_type && formData.qc_event_type && formData.customer_or_company_name && formData.asset_id;
    }
    return true;
  };

  const handleNextStep = () => {
    const hasUpgradesStep = ['Laptop', 'Desktop'].includes(formData.product_category);
    if (currentStep === 1 && !hasUpgradesStep) {
      setCurrentStep(3);
    } else if (currentStep === 3) {
      setCurrentStep(4);
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrevStep = () => {
    const hasUpgradesStep = ['Laptop', 'Desktop'].includes(formData.product_category);
    if (currentStep === 3 && !hasUpgradesStep) {
      setCurrentStep(1);
    } else if (currentStep === 4) {
      setCurrentStep(3);
    } else {
      setCurrentStep((prev) => Math.max(1, prev - 1));
    }
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="qc-wizard">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          {steps.map((step, idx) => {
            const visualStepNum = idx + 1;
            const currentVisual = getCurrentVisualStep();
            const isActive = visualStepNum === currentVisual;
            const isCompleted = visualStepNum < currentVisual;
            
            return (
              <div key={step.number} className="flex items-center flex-1">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-bold transition-colors ${
                      isActive || isCompleted
                        ? 'bg-primary text-white'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {isCompleted ? <Check className="w-6 h-6" /> : visualStepNum}
                  </div>
                  <span className="text-xs mt-2 font-medium text-center hidden md:block">{step.title}</span>
                </div>
                {idx < steps.length - 1 && (
                  <div className={`h-1 flex-1 mx-2 ${isCompleted ? 'bg-primary' : 'bg-muted'}`}></div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-border shadow-sm p-6 md:p-8">
        {currentStep === 1 && (
          <div className="space-y-6" data-testid="step-order-context">
            <h2 className="text-3xl font-bold mb-6">Order Context</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-base font-medium mb-2">Order Type *</Label>
                <Select value={formData.order_type} onValueChange={(v) => setFormData({ ...formData, order_type: v })}>
                  <SelectTrigger data-testid="order-type-select" className="h-14">
                    <SelectValue placeholder="Select order type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Rental">Rental</SelectItem>
                    <SelectItem value="Replacement">Replacement</SelectItem>
                    <SelectItem value="Sale">Sale</SelectItem>
                    <SelectItem value="Service">Service</SelectItem>
                    <SelectItem value="Stockin House">Stock In House</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-base font-medium mb-2">QC Event Type *</Label>
                <Select value={formData.qc_event_type} onValueChange={(v) => setFormData({ ...formData, qc_event_type: v })}>
                  <SelectTrigger data-testid="qc-event-type-select" className="h-14">
                    <SelectValue placeholder="Select event type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Dispatch">Dispatch</SelectItem>
                    <SelectItem value="Return">Return</SelectItem>
                    <SelectItem value="StockIn House">Stock in House</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="relative">
              <Label className="text-base font-medium mb-2">
                Customer / Company Name *
              </Label>
              <SearchableSelect
                items={customerItems}
                value={formData.customer_or_company_name}
                onValueChange={handleCustomerSelect}
                placeholder={customersLoading ? "Loading customers..." : "Select customer"}
                searchPlaceholder="Type to search customers..."
                emptyText="No customers found."
                loading={customersLoading}
                data-testid="customer-name-input"
                renderItem={(item) => (
                  <div className="flex flex-col">
                    <span className="font-medium">{item.label}</span>
                    <span className="text-xs text-muted-foreground">
                      {item.contact_person} {item.phone_number ? `\u2022 ${item.phone_number}` : ''}
                    </span>
                  </div>
                )}
              />
            </div>

            <div className="relative">
              <Label className="text-base font-medium mb-2">Select Asset *</Label>
              <SearchableSelect
                items={assetItems}
                value={formData.asset_id}
                onValueChange={handleAssetSelect}
                placeholder={assetsLoading ? "Loading assets..." : "Select asset"}
                searchPlaceholder="Type to search assets..."
                emptyText="No assets found."
                loading={assetsLoading}
                data-testid="asset-select-input"
                renderItem={(item) => (
                  <div className="flex flex-col">
                    <span className="font-medium">{item.label}</span>
                    <span className="text-xs text-muted-foreground">
                      S/N: {item.serial_number} &bull; {item.asset_type}
                    </span>
                  </div>
                )}
              />
            </div>

            <div>
              <Label className="text-base font-medium mb-2">Remarks</Label>
              <Textarea
                placeholder="Enter configuration details, notes, or any additional remarks..."
                value={formData.remarks}
                onChange={(e) => setFormData((prev) => ({ ...prev, remarks: e.target.value }))}
                className="min-h-[80px]"
                data-testid="remarks-input"
              />
            </div>

            {formData.asset_id && (
              <div className="p-4 bg-muted rounded-lg space-y-2">
                <p><strong>Product Category:</strong> {formData.product_category}</p>
                <p><strong>Model:</strong> {formData.model_number}</p>
                <p><strong>Serial Number:</strong> {formData.serial_number}</p>
              </div>
            )}
          </div>
        )}

        {currentStep === 2 && (
          <div className="space-y-6" data-testid="step-specifications">
            <h2 className="text-3xl font-bold mb-6">RAM/SSD Upgrades (If Any)</h2>

            {['Laptop', 'Desktop'].includes(formData.product_category) ? (
              <>
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-6">
                  <p className="text-sm text-blue-900">
                    <strong>Note:</strong> Only record upgrades here. The asset's original specifications are already stored in the Asset Master.
                  </p>
                </div>

                <div className="border-t pt-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <Switch
                      checked={formData.upgrades_done}
                      onCheckedChange={(v) => setFormData({ ...formData, upgrades_done: v })}
                      data-testid="upgrades-done-toggle"
                    />
                    <Label className="text-lg font-bold">Any Upgrades Done?</Label>
                  </div>

                  {formData.upgrades_done && (
                    <div className="space-y-6 p-4 bg-muted rounded-lg">
                      <div className="space-y-4">
                        <div className="flex items-center space-x-3">
                          <Switch
                            checked={formData.ram_added_or_changed}
                            onCheckedChange={(v) => setFormData({ ...formData, ram_added_or_changed: v })}
                          />
                          <Label>RAM Added / Changed</Label>
                        </div>
                        {formData.ram_added_or_changed && (
                          <div className="pl-8 space-y-3">
                            <div>
                              <Label>New RAM Size</Label>
                              <Select value={formData.ram_size} onValueChange={(v) => setFormData({ ...formData, ram_size: v })}>
                                <SelectTrigger className="h-12">
                                  <SelectValue placeholder="Select RAM size" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="8 GB">8 GB</SelectItem>
                                  <SelectItem value="16 GB">16 GB</SelectItem>
                                  <SelectItem value="32 GB">32 GB</SelectItem>
                                  <SelectItem value="128 GB">128 GB</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label>RAM Type</Label>
                              <Select value={formData.ram_type} onValueChange={(v) => setFormData({ ...formData, ram_type: v })}>
                                <SelectTrigger className="h-12">
                                  <SelectValue placeholder="Select RAM type" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="DDR3">DDR3</SelectItem>
                                  <SelectItem value="DDR4">DDR4</SelectItem>
                                  <SelectItem value="DDR5">DDR5</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <Input
                              placeholder="RAM Serial Number"
                              value={formData.ram_serial_number}
                              onChange={(e) => setFormData({ ...formData, ram_serial_number: e.target.value })}
                              className="h-12"
                            />
                            <div className="flex items-center space-x-3">
                              <Switch
                                checked={formData.old_ram_returned_to_store}
                                onCheckedChange={(v) => setFormData({ ...formData, old_ram_returned_to_store: v })}
                              />
                              <Label>Old RAM Returned to Store</Label>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="space-y-4">
                        <div className="flex items-center space-x-3">
                          <Switch
                            checked={formData.ssd_added_or_changed}
                            onCheckedChange={(v) => setFormData({ ...formData, ssd_added_or_changed: v })}
                          />
                          <Label>SSD Added / Changed</Label>
                        </div>
                        {formData.ssd_added_or_changed && (
                          <div className="pl-8 space-y-3">
                            <div>
                              <Label>New SSD Size</Label>
                              <Select value={formData.ssd_size} onValueChange={(v) => setFormData({ ...formData, ssd_size: v })}>
                                <SelectTrigger className="h-12">
                                  <SelectValue placeholder="Select SSD size" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="128 GB">128 GB</SelectItem>
                                  <SelectItem value="256 GB">256 GB</SelectItem>
                                  <SelectItem value="512 GB">512 GB</SelectItem>
                                  <SelectItem value="1 TB">1 TB</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label>SSD Type</Label>
                              <Select value={formData.ssd_type} onValueChange={(v) => setFormData({ ...formData, ssd_type: v })}>
                                <SelectTrigger className="h-12">
                                  <SelectValue placeholder="Select SSD type" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="SATA SSD">SATA SSD</SelectItem>
                                  <SelectItem value="NVMe SSD">NVMe SSD</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <Input
                              placeholder="SSD Serial Number"
                              value={formData.ssd_serial_number}
                              onChange={(e) => setFormData({ ...formData, ssd_serial_number: e.target.value })}
                              className="h-12"
                            />
                            <div className="flex items-center space-x-3">
                              <Switch
                                checked={formData.old_ssd_returned_to_store}
                                onCheckedChange={(v) => setFormData({ ...formData, old_ssd_returned_to_store: v })}
                              />
                              <Label>Old SSD Returned to Store</Label>
                            </div>
                          </div>
                        )}
                      </div>

                      <Textarea
                        placeholder="Upgrade Notes"
                        value={formData.upgrade_notes}
                        onChange={(e) => setFormData({ ...formData, upgrade_notes: e.target.value })}
                        className="min-h-[80px]"
                      />

                      <div>
                        <Label className="mb-2">Upgrade Approved By</Label>
                        <Select value={formData.upgrade_approved_by} onValueChange={(v) => setFormData({ ...formData, upgrade_approved_by: v })}>
                          <SelectTrigger className="h-12">
                            <SelectValue placeholder="Select approver" />
                          </SelectTrigger>
                          <SelectContent>
                            {technicians.map((tech) => (
                              <SelectItem key={tech.id} value={tech.full_name}>
                                {tech.full_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <p className="text-center py-8 text-muted-foreground">
                No upgrade tracking required for {formData.product_category}
              </p>
            )}
          </div>
        )}

        {currentStep === 3 && (
          <div className="space-y-6" data-testid="step-qc-checklist">
            <h2 className="text-3xl font-bold mb-6">QC Checklist</h2>
            
            {validationErrors.length > 0 && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-bold text-red-700">Please complete all mandatory QC checks before submitting.</p>
                  <p className="text-sm text-red-600">{validationErrors.length} checklist item(s) still need to be answered.</p>
                </div>
              </div>
            )}

            {checklist ? (
              <div className="space-y-8">
                {Object.keys(checklist).map((stage) => {
                  if (stage === 'OriginalOSSection') return null;
                  return (
                    <div key={stage} className="border-b pb-6">
                      <h3 className="text-xl font-bold mb-4 text-primary">{stage}</h3>
                      <div className="space-y-4">
                        {checklist[stage].map((item, idx) => {
                          const isUnanswered = validationErrors.some(e => e.stage === stage && e.item === item);
                          const answer = formData.qc_checklist[stage]?.[item]?.answer;
                          
                          return (
                          <div 
                            key={idx} 
                            className={`p-4 rounded-lg space-y-3 ${
                              isUnanswered 
                                ? 'bg-red-50 border-2 border-red-300' 
                                : answer 
                                  ? 'bg-green-50 border border-green-200' 
                                  : 'bg-muted'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <p className="font-medium flex-1">{item} <span className="text-red-500">*</span></p>
                              {answer && <Check className="w-5 h-5 text-green-600 flex-shrink-0" />}
                            </div>
                            <RadioGroup
                              value={answer || ''}
                              onValueChange={(v) => updateChecklistItem(stage, item, 'answer', v)}
                            >
                              <div className="flex space-x-4">
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="Yes" id={`${stage}-${idx}-yes`} />
                                  <Label htmlFor={`${stage}-${idx}-yes`}>Yes</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="No" id={`${stage}-${idx}-no`} />
                                  <Label htmlFor={`${stage}-${idx}-no`}>No</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="NA" id={`${stage}-${idx}-na`} />
                                  <Label htmlFor={`${stage}-${idx}-na`}>N/A</Label>
                                </div>
                              </div>
                            </RadioGroup>
                            <Input
                              placeholder="Notes (optional)"
                              value={formData.qc_checklist[stage]?.[item]?.notes || ''}
                              onChange={(e) => updateChecklistItem(stage, item, 'notes', e.target.value)}
                              className="h-10"
                            />
                          </div>
                        )})}
                      </div>
                    </div>
                  );
                })}

                {/* Dedicated Original OS Section at the bottom */}
                <div className="border-b pb-6">
                  <h3 className="text-xl font-bold mb-4 text-primary">OS Verification</h3>
                  <div className="space-y-4">
                    <div 
                      className={`p-4 rounded-lg space-y-3 ${
                        validationErrors.some(e => e.stage === 'OriginalOSSection' && e.item === 'Original OS')
                          ? 'bg-red-50 border-2 border-red-300' 
                          : formData.qc_checklist['OriginalOSSection']?.['Original OS']?.answer 
                            ? 'bg-green-50 border border-green-200' 
                            : 'bg-muted'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <p className="font-medium flex-1">Original OS <span className="text-red-500">*</span></p>
                        {formData.qc_checklist['OriginalOSSection']?.['Original OS']?.answer && <Check className="w-5 h-5 text-green-600 flex-shrink-0" />}
                      </div>
                      <RadioGroup
                        value={formData.qc_checklist['OriginalOSSection']?.['Original OS']?.answer || ''}
                        onValueChange={(v) => updateChecklistItem('OriginalOSSection', 'Original OS', 'answer', v)}
                      >
                        <div className="flex space-x-4">
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Yes" id="orig-os-yes" />
                            <Label htmlFor="orig-os-yes">Yes</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="No" id="orig-os-no" />
                            <Label htmlFor="orig-os-no">No</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="NA" id="orig-os-na" />
                            <Label htmlFor="orig-os-na">N/A</Label>
                          </div>
                        </div>
                      </RadioGroup>
                      <Input
                        placeholder="Notes (optional)"
                        value={formData.qc_checklist['OriginalOSSection']?.['Original OS']?.notes || ''}
                        onChange={(e) => updateChecklistItem('OriginalOSSection', 'Original OS', 'notes', e.target.value)}
                        className="h-10"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-center py-8 text-muted-foreground">Loading checklist...</p>
            )}
          </div>
        )}

        {currentStep === 4 && (
          <div className="space-y-6" data-testid="step-photos-review">
            <h2 className="text-3xl font-bold mb-6">Photos & Review</h2>

            <div>
              <Label className="text-lg font-bold mb-3">Upload Photos</Label>
              
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoUpload}
                className="hidden"
                data-testid="camera-input"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
                className="hidden"
                data-testid="gallery-input"
              />
              
              <div className="border-2 border-dashed border-border rounded-lg p-6">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCameraCapture}
                    className="h-24 flex flex-col items-center justify-center space-y-2"
                    data-testid="take-photo-btn"
                  >
                    <Camera className="w-8 h-8 text-primary" />
                    <span className="text-sm font-medium">Take Photo</span>
                  </Button>
                  
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleGalleryUpload}
                    className="h-24 flex flex-col items-center justify-center space-y-2"
                    data-testid="upload-photo-btn"
                  >
                    <ImagePlus className="w-8 h-8 text-primary" />
                    <span className="text-sm font-medium">From Gallery</span>
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground text-center">Upload 2-10 photos (auto-compressed for storage)</p>
              </div>
              
              {formData.photos.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="font-medium">{formData.photos.length} photo(s) uploaded:</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {formData.photos.map((photo, idx) => (
                      <div key={idx} className="relative group">
                        <img
                          src={photo.data}
                          alt={`Upload ${idx + 1}`}
                          className="w-full h-24 object-cover rounded-lg border"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-1 rounded-b-lg">
                          {photo.formatted_timestamp || 'No timestamp'}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              photos: prev.photos.filter((_, i) => i !== idx),
                            }));
                          }}
                          className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label className="text-lg font-bold mb-3">Assign Second Check Technician *</Label>
              <Select
                value={formData.second_check_technician}
                onValueChange={(v) => setFormData({ ...formData, second_check_technician: v })}
              >
                <SelectTrigger data-testid="second-checker-select" className="h-14">
                  <SelectValue placeholder="Select second checker" />
                </SelectTrigger>
                <SelectContent>
                  {technicians.map((tech) => (
                    <SelectItem key={tech.id} value={tech.full_name}>
                      {tech.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="p-6 bg-muted rounded-lg space-y-2">
              <h3 className="font-bold text-lg mb-3">Summary</h3>
              <p><strong>Customer:</strong> {formData.customer_or_company_name}</p>
              <p><strong>Asset:</strong> {formData.asset_id}</p>
              <p><strong>Product:</strong> {formData.product_category}</p>
              <p><strong>Event:</strong> {formData.qc_event_type}</p>
              <p><strong>Original OS:</strong> {formData.qc_checklist['OriginalOSSection']?.['Original OS']?.answer || 'Not answered'}</p>
              <p><strong>Second Checker:</strong> {formData.second_check_technician || 'Not assigned'}</p>
              {formData.remarks && <p><strong>Remarks:</strong> {formData.remarks}</p>}
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-8">
        <Button
          variant="outline"
          onClick={handlePrevStep}
          disabled={currentStep === 1}
          data-testid="prev-step-button"
          className="h-14 px-8"
        >
          <ChevronLeft className="w-5 h-5 mr-2" />
          Previous
        </Button>

        {!isLastStep() ? (
          <Button
            onClick={handleNextStep}
            disabled={!canProceed()}
            data-testid="next-step-button"
            className="h-14 px-8 font-bold uppercase"
          >
            Next
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={loading}
            data-testid="submit-qc-button"
            className="h-14 px-8 font-bold uppercase shadow-lg shadow-primary/20"
          >
            {loading ? 'Submitting...' : 'Submit QC'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default QCWizardPage;